import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { CheckCircle2Icon, XCircleIcon } from "lucide-react";
import { ReadUsersUsersGetResponse, UserClientPermissionDetail } from "@/client-admin/types.gen.ts";
import { DataTableColumnHeader } from "../UserTable/data-table-column-header";
import {
  estimatedPricePreferenceDefaultvalues,
  estimatedPriceUpdatePreferencesSchema,
} from "../UserTable/PricePreference/schema";
import { DeleteUserClientLinkDialog } from "./DeleteUserClientLinkDialog";
import { EditUserClientLinkForm } from "./EditUserClientLinkForm";

export type UserWithClients = ReadUsersUsersGetResponse["data"][number];

export const columns: ColumnDef<UserClientPermissionDetail>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="User ID" />,
    cell: ({ row }) => <div>{row.getValue("id")}</div>,
  },
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => <div>{row.getValue("email")}</div>,
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Active" />,
    cell: ({ row }) => (
      <div>
        {row.getValue("is_active") ? (
          <CheckCircle2Icon className="h-5 w-5 text-green-500" />
        ) : (
          <XCircleIcon className="h-5 w-5 text-red-500" />
        )}
      </div>
    ),
  },

  {
    accessorKey: "client_name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Client name" />,
    cell: ({ row }) => <div>{row.getValue("client_name")}</div>,
    filterFn: (row, _, value: number | null) => {
      if (value === null) return true;
      return row.getValue("client_id") === value;
    },
  },
  {
    accessorKey: "client_code",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Client Code" />,
    cell: ({ row }) => <div>{row.getValue("client_code")}</div>,
    filterFn: (row, _, value: string | null) => {
      if (value === null) return true;
      return row.getValue("client_code") === value;
    },
  },
  {
    accessorKey: "client_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Client ID" />,
    cell: ({ row }) => <div>{row.getValue("client_id")}</div>,
  },

  {
    accessorKey: "permissions",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Permissions" />,
    cell: ({ row }) => {
      const permissions = row.getValue("permissions") as string[] | null;
      return <div>{permissions ? permissions.join(", ") : "-"}</div>;
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created At" />,
    cell: ({ row }) => {
      const date = new Date(row.getValue("created_at"));
      return <div>{format(date, "yyyy-MM-dd HH:mm:ss")}</div>;
    },
  },
  {
    accessorKey: "updated_at",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Updated At" />,
    cell: ({ row }) => {
      const date = new Date(row.getValue("updated_at"));
      return <div>{format(date, "yyyy-MM-dd HH:mm:ss")}</div>;
    },
  },
  {
    id: "edit",
    header: () => <div className="flex w-full items-center space-x-2">Edit</div>,
    cell: ({ row }) => {
      return (
        <EditUserClientLinkForm
          defaultValues={{
            permissions: row.getValue("permissions"),
            email: row.getValue("email"),
            id: row.getValue("id"),
            client_id: row.getValue("client_id"),
            client_code: row.getValue("client_code"),
            cc_recipients: row.original.cc_recipients || [],
            bcc_recipients: row.original.bcc_recipients || [],
            estimated_price_update_preferences: (() => {
              const maybe = estimatedPriceUpdatePreferencesSchema.safeParse(
                row.original.estimated_price_update_preferences,
              );
              if (maybe.success) {
                return maybe.data;
              }
              return estimatedPricePreferenceDefaultvalues;
            })(),
          }}
        />
      );
    },
  },
  {
    id: "delete",
    header: () => <div className="flex w-full items-center space-x-2">Delete</div>,
    cell: ({ row }) => <DeleteUserClientLinkDialog userId={row.getValue("id")} clientId={row.getValue("client_id")} />,
  },
];
